import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
  FiSearch,
  FiShield,
  FiRefreshCw,
  FiGift,
  FiTrash2,
  FiX,
  FiAlertCircle,
  FiCheck,
} from "react-icons/fi";

function AdminUsers() {
  // Application selector
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [loadingApps, setLoadingApps] = useState(true);

  // Search
  const [searchEmail, setSearchEmail] = useState("");
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);

  // Feedback
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modals
  const [accessModal, setAccessModal] = useState(null);
  const [grantModal, setGrantModal] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  // Grant form
  const [prices, setPrices] = useState([]);
  const [grantPriceId, setGrantPriceId] = useState("");
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucherId, setSelectedVoucherId] = useState("");
  const [grantNote, setGrantNote] = useState("");

  // Remove confirmation
  const [removeConfirmText, setRemoveConfirmText] = useState("");

  // Load applications on mount
  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoadingApps(true);
    try {
      const response = await api.get("/configuration/applications");
      const apps = response.data.data || [];
      setApplications(apps);
      if (apps.length > 0) {
        setSelectedApp(apps[0].id);
      }
    } catch (err) {
      setError("Failed to load applications");
    } finally {
      setLoadingApps(false);
    }
  };

  const fetchUsers = useCallback(
    async (emailFilter) => {
      if (!selectedApp) return;

      setSearching(true);
      setError(null);
      try {
        let url = `/admin/users?applicationId=${encodeURIComponent(selectedApp)}`;
        if (emailFilter && emailFilter.trim()) {
          url += `&email=${encodeURIComponent(emailFilter.trim())}`;
        }
        const response = await api.get(url);
        setUsers(response.data.data || []);
        if ((response.data.data || []).length === 0) {
          setError(
            emailFilter
              ? "No users found matching that email"
              : "No users found for this application",
          );
        }
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load users");
      } finally {
        setSearching(false);
      }
    },
    [selectedApp],
  );

  // Auto-load users when application changes
  useEffect(() => {
    if (selectedApp) {
      fetchUsers("");
    }
  }, [selectedApp, fetchUsers]);

  const searchUsers = useCallback(() => {
    fetchUsers(searchEmail);
  }, [fetchUsers, searchEmail]);

  const handleCheckAccess = async (userId) => {
    setLoadingAction(true);
    setError(null);
    try {
      const response = await api.get(
        `/admin/users/${userId}/access?applicationId=${encodeURIComponent(selectedApp)}`,
      );
      setAccessModal(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to check access");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleResetTokens = async (userId) => {
    if (
      !window.confirm(
        "Reset token balance for this user? This will restore their tokens to the full allowance.",
      )
    ) {
      return;
    }
    setLoadingAction(true);
    setError(null);
    try {
      await api.post(`/admin/users/${userId}/reset-tokens`, {
        applicationId: selectedApp,
      });
      setSuccess("Token balance reset successfully");
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers(searchEmail); // Refresh results
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reset tokens");
    } finally {
      setLoadingAction(false);
    }
  };

  const openGrantModal = async (user) => {
    setGrantModal(user);
    setGrantPriceId("");
    setSelectedVoucherId("");
    setGrantNote("");
    setPrices([]);
    setVouchers([]);
    // Fetch prices and vouchers independently
    try {
      const pricesRes = await api.get(
        `/admin/prices?applicationId=${encodeURIComponent(selectedApp)}`,
      );
      // Deduplicate by product — for grants, billing period is irrelevant
      const allPrices = pricesRes.data.data || [];
      const seen = new Set();
      const uniquePrices = allPrices.filter((p) => {
        if (seen.has(p.product_id)) return false;
        seen.add(p.product_id);
        return true;
      });
      setPrices(uniquePrices);
      // Auto-select if only one plan
      if (uniquePrices.length === 1) {
        setGrantPriceId(uniquePrices[0].id);
      }
    } catch (err) {
      setError("Failed to load pricing plans");
    }
    try {
      const vouchersRes = await api.get(
        `/admin/vouchers?applicationId=${encodeURIComponent(selectedApp)}`,
      );
      // Only show 100% discount vouchers for grant subscriptions
      const allVouchers = vouchersRes.data.data || [];
      const fullDiscountVouchers = allVouchers.filter(
        (v) => v.discount_type === "percentage" && v.discount_value === 100,
      );
      setVouchers(fullDiscountVouchers);
      if (fullDiscountVouchers.length === 0) {
        console.warn(
          "No 100% discount vouchers for app:",
          selectedApp,
          `(${allVouchers.length} total vouchers found)`,
        );
      }
    } catch (err) {
      console.error(
        "Failed to load vouchers:",
        err.response?.data || err.message,
      );
      setError(
        "Failed to load vouchers: " +
          (err.response?.data?.error || err.message),
      );
    }
  };

  const handleGrantSubscription = async () => {
    const selectedVoucher = vouchers.find((v) => v.id === selectedVoucherId);
    if (!grantPriceId || !grantModal || !selectedVoucher) return;
    setLoadingAction(true);
    setError(null);
    try {
      await api.post(`/admin/users/${grantModal.user_id}/grant-subscription`, {
        applicationId: selectedApp,
        priceId: grantPriceId,
        voucherCode: selectedVoucher.code,
        note: grantNote || undefined,
      });
      setSuccess("Subscription granted successfully");
      setTimeout(() => setSuccess(null), 3000);
      setGrantModal(null);
      fetchUsers(searchEmail);
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.error ||
        (data?.errors &&
          data.errors.map((e) => e.msg || e.message).join(", ")) ||
        "Failed to grant subscription";
      setError(msg);
    } finally {
      setLoadingAction(false);
    }
  };

  const openRemoveModal = (user) => {
    setRemoveModal(user);
    setRemoveConfirmText("");
  };

  const handleRemoveAll = async () => {
    if (removeConfirmText !== "REMOVE" || !removeModal) return;
    setLoadingAction(true);
    setError(null);
    try {
      await api.delete(`/admin/users/${removeModal.user_id}/subscriptions`, {
        data: { applicationId: selectedApp },
      });
      setSuccess("All subscriptions and data removed");
      setTimeout(() => setSuccess(null), 3000);
      setRemoveModal(null);
      fetchUsers(searchEmail);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove subscriptions");
    } finally {
      setLoadingAction(false);
    }
  };

  const getStatusBadge = (user) => {
    // Active trial always takes display priority — even if a subscription row also exists
    if (user.user_trial_end) {
      const trialEnd = new Date(user.user_trial_end);
      if (trialEnd > new Date()) {
        return <span className="badge badge-warning">Trial</span>;
      }
    }

    if (!user.subscription_status) {
      if (user.user_trial_end) {
        return <span className="badge badge-error">Trial Expired</span>;
      }
      return (
        <span
          className="badge"
          style={{
            background: "var(--bg-tertiary)",
            color: "var(--text-secondary)",
          }}
        >
          No Subscription
        </span>
      );
    }

    switch (user.subscription_status) {
      case "active":
        return <span className="badge badge-success">Active</span>;
      case "granted":
        return (
          <span
            className="badge"
            style={{ background: "#3b82f6", color: "#fff" }}
          >
            Granted
          </span>
        );
      case "trialing":
        return <span className="badge badge-warning">Trialing</span>;
      case "canceled":
        return <span className="badge badge-error">Canceled</span>;
      case "past_due":
        return <span className="badge badge-error">Past Due</span>;
      default:
        return (
          <span className="badge" style={{ background: "var(--bg-tertiary)" }}>
            {user.subscription_status}
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount, currency) => {
    if (!amount) return "-";
    return `${(amount / 100).toFixed(2)} ${(currency || "").toUpperCase()}`;
  };

  if (loadingApps) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>User Management</h2>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <FiAlertCircle /> {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            <FiX />
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <FiCheck /> {success}
        </div>
      )}

      {/* Search Section */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div
            className="form-group"
            style={{ marginBottom: 0, minWidth: 200 }}
          >
            <label className="form-label">Application</label>
            <select
              className="form-input"
              value={selectedApp}
              onChange={(e) => {
                setSelectedApp(e.target.value);
                setSearchEmail("");
              }}
            >
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name || app.id}
                </option>
              ))}
            </select>
          </div>

          <div
            className="form-group"
            style={{ marginBottom: 0, flex: 1, minWidth: 250 }}
          >
            <label className="form-label">Filter by Email</label>
            <input
              className="form-input"
              type="text"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchUsers()}
              placeholder="Filter users..."
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={searchUsers}
            disabled={searching}
            style={{ height: 46 }}
          >
            {searching ? (
              <div className="spinner" style={{ width: 16, height: 16 }} />
            ) : (
              <FiSearch />
            )}
            {searchEmail.trim() ? "Search" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Results Table */}
      {users.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
          <table className="table" style={{ width: "100%", margin: 0 }}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Expires</th>
                <th>Tokens</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td>
                    <span style={{ fontWeight: 500 }}>
                      {user.email || "No email on file"}
                    </span>
                    <br />
                    <span
                      style={{ fontSize: 12, color: "var(--text-secondary)" }}
                    >
                      {user.user_id.substring(0, 8)}...
                    </span>
                  </td>
                  <td>{getStatusBadge(user)}</td>
                  <td>
                    {user.price_name || user.product_name || "-"}
                    {user.price_amount && (
                      <span
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: "var(--text-secondary)",
                        }}
                      >
                        {formatAmount(user.price_amount, user.currency)}
                      </span>
                    )}
                  </td>
                  <td>
                    {user.user_trial_end &&
                    new Date(user.user_trial_end) > new Date()
                      ? formatDate(user.user_trial_end)
                      : user.subscription_status
                        ? formatDate(user.current_period_end)
                        : user.user_trial_end
                          ? formatDate(user.user_trial_end)
                          : "-"}
                  </td>
                  <td>
                    {user.tokens_used != null
                      ? `${user.tokens_used.toLocaleString()} / ${parseInt(user.token_limit || 0).toLocaleString()}`
                      : "-"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        className="btn btn-sm"
                        onClick={() => handleCheckAccess(user.user_id)}
                        disabled={loadingAction}
                        title="Check Access"
                        style={{ background: "var(--bg-tertiary)" }}
                      >
                        <FiShield />
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleResetTokens(user.user_id)}
                        disabled={loadingAction || user.token_limit == null}
                        title="Reset Tokens"
                        style={{ background: "var(--bg-tertiary)" }}
                      >
                        <FiRefreshCw />
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => openGrantModal(user)}
                        disabled={loadingAction}
                        title="Grant Subscription"
                        style={{ background: "var(--primary)", color: "#fff" }}
                      >
                        <FiGift />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => openRemoveModal(user)}
                        disabled={loadingAction}
                        title="Remove All"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Access Check Modal */}
      {accessModal && (
        <div className="modal-overlay" onClick={() => setAccessModal(null)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">Access Check Result</h3>
              <button
                className="modal-close"
                onClick={() => setAccessModal(null)}
              >
                <FiX />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "var(--text-secondary)" }}>
                  Has Access
                </span>
                <span
                  className={`badge ${accessModal.hasAccess ? "badge-success" : "badge-error"}`}
                >
                  {accessModal.hasAccess ? "Yes" : "No"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-secondary)" }}>Reason</span>
                <span>{accessModal.reason || "-"}</span>
              </div>
              {accessModal.expiresAt && (
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Expires
                  </span>
                  <span>{formatDate(accessModal.expiresAt)}</span>
                </div>
              )}
              {accessModal.subscription && (
                <>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid var(--border-color)",
                    }}
                  />
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>
                      Subscription Status
                    </span>
                    <span>{accessModal.subscription.status}</span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>
                      Product
                    </span>
                    <span>{accessModal.subscription.productName || "-"}</span>
                  </div>
                </>
              )}
              {accessModal.tokenBalance != null && (
                <>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid var(--border-color)",
                    }}
                  />
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>
                      Tokens Used
                    </span>
                    <span>
                      {Math.max(
                        0,
                        (accessModal.tokenLimit || 0) -
                          (accessModal.tokenBalance || 0),
                      ).toLocaleString()}{" "}
                      /{" "}
                      {(accessModal.tokenLimit ?? "-").toLocaleString?.() ??
                        accessModal.tokenLimit ??
                        "-"}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>
                      Enforcement
                    </span>
                    <span>{accessModal.tokenEnforcement || "-"}</span>
                  </div>
                </>
              )}
              {accessModal.trial && (
                <>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid var(--border-color)",
                    }}
                  />
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>
                      Trial End
                    </span>
                    <span>{formatDate(accessModal.trial.trialEnd)}</span>
                  </div>
                </>
              )}
            </div>
            <div style={{ marginTop: 20 }}>
              <details>
                <summary
                  style={{
                    cursor: "pointer",
                    color: "var(--text-secondary)",
                    fontSize: 13,
                  }}
                >
                  Raw JSON
                </summary>
                <pre
                  style={{
                    marginTop: 8,
                    padding: 12,
                    background: "var(--bg-primary)",
                    borderRadius: 8,
                    fontSize: 12,
                    overflow: "auto",
                    maxHeight: 300,
                  }}
                >
                  {JSON.stringify(accessModal, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* Grant Subscription Modal */}
      {grantModal && (
        <div className="modal-overlay" onClick={() => setGrantModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Grant Subscription</h3>
              <button
                className="modal-close"
                onClick={() => setGrantModal(null)}
              >
                <FiX />
              </button>
            </div>

            <div
              style={{
                padding: "12px 16px",
                background: "rgba(99, 102, 241, 0.1)",
                border: "1px solid rgba(99, 102, 241, 0.3)",
                borderRadius: 8,
                marginBottom: 20,
                fontSize: 14,
                color: "var(--text-secondary)",
              }}
            >
              Granting access to{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {grantModal.email}
              </strong>{" "}
              via voucher.
              {(() => {
                const sv = vouchers.find((v) => v.id === selectedVoucherId);
                if (sv && sv.valid_until) {
                  return (
                    <>
                      {" "}
                      Subscription will be active until{" "}
                      <strong style={{ color: "var(--text-primary)" }}>
                        {new Date(sv.valid_until).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </strong>
                      .
                    </>
                  );
                }
                return " Select a voucher and plan below.";
              })()}
            </div>

            <div className="form-group">
              <label className="form-label">
                Plan (features & token allowance)
              </label>
              <select
                className="form-input"
                value={grantPriceId}
                onChange={(e) => setGrantPriceId(e.target.value)}
              >
                <option value="">Select a plan...</option>
                {prices.map((price) => (
                  <option key={price.id} value={price.id}>
                    {price.product_name || price.name || price.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Voucher</label>
              {vouchers.length === 0 ? (
                <div
                  style={{
                    padding: "10px 14px",
                    background: "var(--bg-tertiary)",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "var(--text-secondary)",
                  }}
                >
                  No active 100% discount vouchers available for this app.
                  Create one in the Subscriptions page first.
                </div>
              ) : (
                <select
                  className="form-input"
                  value={selectedVoucherId}
                  onChange={(e) => setSelectedVoucherId(e.target.value)}
                >
                  <option value="">Select a voucher...</option>
                  {vouchers.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.code} —{" "}
                      {v.discount_type === "percentage"
                        ? `${v.discount_value}% off`
                        : `$${(v.discount_value / 100).toFixed(2)} off`}
                      {v.valid_until
                        ? ` — until ${new Date(
                            v.valid_until,
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}`
                        : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Voucher + Price summary */}
            {selectedVoucherId &&
              grantPriceId &&
              (() => {
                const sv = vouchers.find((v) => v.id === selectedVoucherId);
                const sp = prices.find((p) => p.id === grantPriceId);
                if (!sv || !sp) return null;
                const discountAmt =
                  sv.discount_type === "percentage"
                    ? Math.round((sp.amount * sv.discount_value) / 100)
                    : Math.min(sv.discount_value, sp.amount);
                const finalAmt = sp.amount - discountAmt;
                return (
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "rgba(34, 197, 94, 0.1)",
                      border: "1px solid rgba(34, 197, 94, 0.3)",
                      borderRadius: 8,
                      marginBottom: 16,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ marginBottom: 4 }}>
                      <strong>Discount:</strong>{" "}
                      {sv.discount_type === "percentage"
                        ? `${sv.discount_value}%`
                        : `$${(sv.discount_value / 100).toFixed(2)}`}{" "}
                      off {sp.name || sp.product_name} ($
                      {(discountAmt / 100).toFixed(2)} discount on $
                      {(sp.amount / 100).toFixed(2)})
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <strong>Final price:</strong> $
                      {(finalAmt / 100).toFixed(2)}
                      {finalAmt === 0 && (
                        <span style={{ color: "#22c55e", marginLeft: 6 }}>
                          FREE
                        </span>
                      )}
                    </div>
                    {sv.valid_until && (
                      <div>
                        <strong>Access until:</strong>{" "}
                        {new Date(sv.valid_until).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

            <div className="form-group">
              <label className="form-label">Note (optional)</label>
              <input
                className="form-input"
                type="text"
                value={grantNote}
                onChange={(e) => setGrantNote(e.target.value)}
                placeholder="e.g. Beta tester, promotional access"
              />
            </div>

            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <button
                className="btn"
                onClick={() => setGrantModal(null)}
                style={{ background: "var(--bg-tertiary)" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleGrantSubscription}
                disabled={!grantPriceId || !selectedVoucherId || loadingAction}
              >
                {loadingAction ? (
                  <div className="spinner" style={{ width: 16, height: 16 }} />
                ) : (
                  <FiGift />
                )}
                Grant Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove All Modal */}
      {removeModal && (
        <div className="modal-overlay" onClick={() => setRemoveModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title" style={{ color: "var(--error)" }}>
                Remove All Subscriptions
              </h3>
              <button
                className="modal-close"
                onClick={() => setRemoveModal(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="alert alert-error" style={{ marginBottom: 20 }}>
              <FiAlertCircle />
              This will permanently delete all subscriptions, trials, token
              balances, and usage records for{" "}
              <strong>{removeModal.email}</strong> in this application.
            </div>

            <div className="form-group">
              <label className="form-label">
                Type <strong>REMOVE</strong> to confirm
              </label>
              <input
                className="form-input"
                type="text"
                value={removeConfirmText}
                onChange={(e) => setRemoveConfirmText(e.target.value)}
                placeholder="REMOVE"
              />
            </div>

            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <button
                className="btn"
                onClick={() => setRemoveModal(null)}
                style={{ background: "var(--bg-tertiary)" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleRemoveAll}
                disabled={removeConfirmText !== "REMOVE" || loadingAction}
              >
                {loadingAction ? (
                  <div className="spinner" style={{ width: 16, height: 16 }} />
                ) : (
                  <FiTrash2 />
                )}
                Remove Everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
